import { z } from 'zod'

import ENDPOINTS from '@/config/http/endpoints'
import { parseResponse } from '@/config/http/http.helpers'
import {
  ApiGetCitySchema,
  ApiGetSubCitySchema,
  ApiRegistrationDTOSchema
} from '@/ducks/registration/registration.schema'
import { City, Country, Cuisine } from '@/ducks/registration/registration.types'
import { rootApi } from '@/ducks/root.api'

export const registrationApi = rootApi.injectEndpoints({
  endpoints: build => {
    return {
      fetchMarketPresence: build.query<
        { countries: Country[]; cities: City[]; cuisines: Cuisine[] },
        void
      >({
        query: () => ({
          method: 'GET',
          url: ENDPOINTS.GET_CITIES
        }),
        transformResponse: (response: z.infer<typeof ApiGetCitySchema>) => {
          const parsedResponse = parseResponse(response, ApiGetCitySchema)
          return {
            countries: parsedResponse[3].map(countryObj => countryObj.country),
            cities: parsedResponse[0]?.sort((a, b) =>
              a.cityname.localeCompare(b.cityname)
            ),
            cuisines: parsedResponse[1]
          }
        }
      }),
      fetchCuisinesBySubCity: build.query<Cuisine[] | null, string>({
        query: (cityName: string) => ({
          method: 'POST',
          url: ENDPOINTS.GET_SUB_CITIES,
          data: { value: cityName }
        }),
        transformResponse: (response: z.infer<typeof ApiGetSubCitySchema>) => {
          const parsedResponse = parseResponse(response, ApiGetSubCitySchema)
          return parsedResponse[1] !== 0 ? parsedResponse[1] : null
        }
      }),
      registerStore: build.mutation<
        unknown,
        z.infer<typeof ApiRegistrationDTOSchema>
      >({
        query: DTO => ({
          method: 'POST',
          url: ENDPOINTS.REGISTER,
          data: DTO
        })
      }),
      sendRestaurantVerificationEmail: build.mutation<
        { status: number; message: string; errorCode?: string,retryAfter?: number,resendCount?: number },
        { email: string; phone: string; userName?: string; resend?: boolean }
      >({
        query: ({ email, phone, userName, resend }) => ({
          method: 'POST',
          url: ENDPOINTS.SEND_VERIFICATION_EMAIL,
          data: {
            email,
            phone,
            userName,
            resend
          }
        })
      }),
      verifyEmailOtp: build.mutation<
        { status: number; message: string; errorCode?: string; attempts?: number; retryAfter?: number },
        { email: string; code: string }
      >({
        query: ({ email, code }) => ({
          method: 'POST',
          url: ENDPOINTS.VERIFY_EMAIL_OTP,
          data: {
            email,
            otp: code
          }
        })
      })
    }
  }
})

export const {
  useFetchMarketPresenceQuery,
  useLazyFetchCuisinesBySubCityQuery,
  useRegisterStoreMutation,
  useSendRestaurantVerificationEmailMutation,
  useVerifyEmailOtpMutation
} = registrationApi
